/**
 * Integral tweens used by Phira's 170 speed integrator in Modern sub-mode (F§7.3): the height follows the integral
 * of the eased speed, so the integrator needs I(x) = integral from 0 to x of f.
 *
 *  - IntStaticTween:  I(x) per tween id. Plain ids: 0 -> 0, 1 -> x, 2 -> x^2 / 2. Families from their In base:
 *      sine  x - sin(x pi / 2) * 2 / pi          quad x^3/3, cubic x^4/4, quart x^5/5, quint x^6/6
 *      expo  (2^(10x - 10) - 2^-10) / (10 ln 2)  circ x - (x sqrt(1 - x^2) + asin x) / 2
 *      back  (C3 x / 4 - C1 / 3) x^3             elastic F(x) - F(0),
 *            F(x) = -(2^(10x - 10) / (10 (a^2 + b^2))) (a sin v - b cos v), a = ln 2, b = 2 pi / 3,
 *            v = (10x - 10.75) b
 *      bounce: piecewise antiderivative of 1 - B(1 - x) (derived here: x - G(1) + G(1 - x) with G the
 *            antiderivative of the four-parabola out-bounce B, G(0) = 0)
 *    and the variants Out(I)(x) = x + I(1 - x) - I(1), InOut(I)(x) = 2x < 1 ? I(2x) / 4 : x - 0.5 + I(2 - 2x) / 4.
 *  - IntClampedTween(id, L..R): [I(L + (R-L)x) - I(L) - f(L) ((L + (R-L)x) - L)] / ((R - L) (f(R) - f(L))),
 *    or x^2 / 2 when f(R) is within 1e-5 of f(L) (the integral of the linear fallback).
 *  - GeneralIntTween (bezier): 3-point Gauss-Legendre, y(x) = (x/2) sum w_i f((x/2)(nu_i + 1)).
 *
 * Every step is rounded to f32 (the tween outputs are f32). The research documents give the formulas but not
 * Phira's exact operation order inside each integral; the order used here follows the formulas as written, so
 * results agree with Phira to f32 rounding.
 */

import {
  BACK_C1,
  BACK_C3,
  BOUNCE_N1,
  BOUNCE_PIECES,
  ELASTIC_C4,
  PI_F32,
  staticY,
  TWEEN_FAMILIES,
  assertTweenId,
} from './tweens';
import type { EaseFamily, TweenId } from './types';

const fr = Math.fround;

type IntFn = (x: number) => number;

const LN2_F32 = fr(Math.LN2);
const TWO_POW_M10 = fr(Math.pow(2, -10));
const EXPO_DEN = fr(10 * LN2_F32);

function intSine(x: number): number {
  return fr(x - fr(fr(fr(Math.sin(fr(fr(x * PI_F32) / 2))) * 2) / PI_F32));
}

function intQuad(x: number): number {
  return fr(fr(fr(x * x) * x) / 3);
}

function intCubic(x: number): number {
  return fr(fr(fr(fr(x * x) * x) * x) / 4);
}

function intQuart(x: number): number {
  return fr(fr(fr(fr(fr(x * x) * x) * x) * x) / 5);
}

function intQuint(x: number): number {
  return fr(fr(fr(fr(fr(fr(x * x) * x) * x) * x) * x) / 6);
}

function intExpo(x: number): number {
  return fr(fr(fr(Math.pow(2, fr(fr(10 * x) - 10))) - TWO_POW_M10) / EXPO_DEN);
}

function intCirc(x: number): number {
  const s = fr(Math.sqrt(fr(1 - fr(x * x))));
  return fr(x - fr(fr(fr(x * s) + fr(Math.asin(x))) / 2));
}

function intBack(x: number): number {
  const lead = fr(fr(fr(BACK_C3 * x) / 4) - fr(BACK_C1 / 3));
  return fr(lead * fr(fr(x * x) * x));
}

const ELASTIC_A = LN2_F32;
const ELASTIC_B = ELASTIC_C4;
const ELASTIC_DEN = fr(10 * fr(fr(ELASTIC_A * ELASTIC_A) + fr(ELASTIC_B * ELASTIC_B)));

function elasticAntiderivative(x: number): number {
  const v = fr(fr(fr(10 * x) - 10.75) * ELASTIC_B);
  const amp = fr(fr(Math.pow(2, fr(fr(10 * x) - 10))) / ELASTIC_DEN);
  const osc = fr(fr(ELASTIC_A * fr(Math.sin(v))) - fr(ELASTIC_B * fr(Math.cos(v))));
  return -fr(amp * osc);
}

const ELASTIC_F0 = elasticAntiderivative(0);

function intElastic(x: number): number {
  return fr(elasticAntiderivative(x) - ELASTIC_F0);
}

// Bounce: B(u) = N (u - c_k)^2 + a_k on piece k; H_k(u) = N (u - c_k)^3 / 3 + a_k u; G is continuous with G(0) = 0.
interface BouncePiece {
  readonly start: number;
  readonly c: number;
  readonly a: number;
  /** G(start) (f32). */
  readonly g0: number;
}

function bouncePieces(): readonly BouncePiece[] {
  const { t1, t2, t3, o2, o3, o4 } = BOUNCE_PIECES;
  const defs: readonly (readonly [start: number, c: number, a: number])[] = [
    [0, 0, 0],
    [t1, o2, 0.75],
    [t2, o3, 0.9375],
    [t3, o4, 0.984375],
  ];
  const exactH = (c: number, a: number, u: number): number => (BOUNCE_N1 * (u - c) ** 3) / 3 + a * u;
  const out: BouncePiece[] = [];
  let g = 0;
  for (let i = 0; i < defs.length; i++) {
    const def = defs[i];
    if (def === undefined) break;
    const [start, c, a] = def;
    out.push({ start, c, a, g0: fr(g) });
    const next = defs[i + 1];
    if (next !== undefined) g += exactH(c, a, next[0]) - exactH(c, a, start);
  }
  return out;
}

const BOUNCE_G_PIECES = bouncePieces();

function bounceH(piece: BouncePiece, u: number): number {
  const d = fr(u - piece.c);
  return fr(fr(fr(BOUNCE_N1 * fr(fr(d * d) * d)) / 3) + fr(piece.a * u));
}

/** G(u) = integral from 0 to u of the out-bounce base B. */
function bounceG(u: number): number {
  let piece = BOUNCE_G_PIECES[0];
  for (const p of BOUNCE_G_PIECES) if (u >= p.start) piece = p;
  if (piece === undefined) return Number.NaN;
  return fr(piece.g0 + fr(bounceH(piece, u) - bounceH(piece, piece.start)));
}

const BOUNCE_G1 = bounceG(1);

function intBounce(x: number): number {
  return fr(fr(x - BOUNCE_G1) + bounceG(fr(1 - x)));
}

const INT_BASES: Readonly<Record<Exclude<EaseFamily, 'linear'>, IntFn>> = {
  sine: intSine,
  quad: intQuad,
  cubic: intCubic,
  quart: intQuart,
  quint: intQuint,
  expo: intExpo,
  circ: intCirc,
  back: intBack,
  elastic: intElastic,
  bounce: intBounce,
};

function intOut(base: IntFn): IntFn {
  const total = base(1);
  return (x) => fr(fr(x + base(fr(1 - x))) - total);
}

function intInOut(base: IntFn): IntFn {
  return (x) => {
    const x2 = fr(2 * x);
    return x2 < 1 ? fr(base(x2) / 4) : fr(fr(x - 0.5) + fr(base(fr(2 - x2)) / 4));
  };
}

function buildIntTable(): readonly IntFn[] {
  const table: IntFn[] = [() => 0, (x) => x, (x) => fr(fr(x * x) / 2)];
  for (const family of TWEEN_FAMILIES) {
    const base = INT_BASES[family];
    table.push(base, intOut(base), intInOut(base));
  }
  return table;
}

const INT_TABLE: readonly IntFn[] = buildIntTable();

/** IntStaticTween: I(x) = integral from 0 to x of TWEEN_FUNCTIONS[id] (f32, x unclamped). */
export function intStaticY(id: TweenId, x: number): number {
  assertTweenId(id);
  const f = INT_TABLE[id];
  if (f === undefined) return Number.NaN;
  return f(fr(x));
}

const INT_CLAMPED_EPS = fr(1e-5);

/** IntClampedTween: integral of the clamped tween (id, l..r) from 0 to x (f32). */
export function intClampedY(id: TweenId, l: number, r: number, x: number): number {
  assertTweenId(id);
  const lo = fr(l);
  const hi = fr(r);
  const xx = fr(x);
  const fl = staticY(id, lo);
  const fh = staticY(id, hi);
  const df = fr(fh - fl);
  if (!(Math.abs(df) >= INT_CLAMPED_EPS)) return fr(fr(xx * xx) / 2);
  const span = fr(hi - lo);
  const arg = fr(lo + fr(span * xx));
  const num = fr(fr(intStaticY(id, arg) - intStaticY(id, lo)) - fr(fl * fr(arg - lo)));
  return fr(num / fr(span * df));
}

const GL_NODES: readonly number[] = [fr(-0.7745967), 0, fr(0.7745967)];
const GL_WEIGHTS: readonly number[] = [fr(5 / 9), fr(8 / 9), fr(5 / 9)];

/** GeneralIntTween: 3-point Gauss-Legendre integral of f from 0 to x (f32). */
export function generalIntY(f: (x: number) => number, x: number): number {
  const h = fr(fr(x) / 2);
  let sum = 0;
  for (let i = 0; i < GL_NODES.length; i++) {
    const node = GL_NODES[i] ?? 0;
    const w = GL_WEIGHTS[i] ?? 0;
    sum = fr(sum + fr(w * fr(f(fr(h * fr(node + 1))))));
  }
  return fr(h * sum);
}
